import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SecureStorage } from '../../utils/storage';
import type { RootState } from '../../store/store';

/**
 * Item extras — CLIENT-ONLY mock layer for per-item fields the backend
 * doesn't support yet:
 *   - lending_rules (family_enabled, neighbors_enabled, max_duration_days,
 *     care_level, commercial_prohibited, deposit_required)
 *   - status_badge (e.g. "PREMIUM")
 *   - quantity
 *
 * Matches the pattern used in `features/sharing/groupsSlice.ts`.
 * Persists to SecureStorage so edits survive reloads. When the backend
 * lands these fields on InventoryItem, the thunks here are replaced
 * with `api.updateInventoryItem(...)` calls and the reducer keeps the
 * same shape — surgical swap.
 *
 * See `docs/redesign/BACKEND_GAPS.md` for the finalized API spec.
 */

export type StatusBadge = 'PREMIUM' | 'STANDARD' | null;

export interface LendingRules {
  family_enabled: boolean;
  neighbors_enabled: boolean;
  max_duration_days?: number;
  care_level?: string;
  commercial_prohibited?: boolean;
  deposit_required?: string;
}

export interface ItemExtras {
  item_id: string;           // same key used on the backend (id, as string)
  lending_rules: LendingRules;
  status_badge?: StatusBadge;
  quantity?: number;
  updated_at: string;        // ISO
}

interface ItemExtrasState {
  byId: Record<string, ItemExtras>;
  hydrated: boolean;
}

const STORAGE_KEY = '@founsi/itemExtras.v1';

const initialState: ItemExtrasState = {
  byId: {},
  hydrated: false,
};

const DEFAULT_LENDING_RULES: LendingRules = {
  family_enabled: true,
  neighbors_enabled: false,
};

const persist = async (byId: Record<string, ItemExtras>) => {
  try {
    await SecureStorage.setItem(STORAGE_KEY, JSON.stringify(byId));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('🧪 itemExtrasSlice: persist failed', err);
  }
};

export const hydrateItemExtras = createAsyncThunk(
  'itemExtras/hydrate',
  async () => {
    try {
      const raw = await SecureStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw) as Record<string, ItemExtras>;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('🧪 itemExtrasSlice: hydrate failed', err);
    }
    return {} as Record<string, ItemExtras>;
  },
);

export const updateLendingRules = createAsyncThunk(
  'itemExtras/updateLendingRules',
  async (
    input: { itemId: string; patch: Partial<LendingRules> },
    { getState },
  ) => {
    // eslint-disable-next-line no-console
    console.log('🧪 itemExtrasSlice: updateLendingRules', input);
    const state = getState() as RootState;
    const existing = state.itemExtras.byId[input.itemId];
    const next: ItemExtras = {
      item_id: input.itemId,
      lending_rules: {
        ...DEFAULT_LENDING_RULES,
        ...(existing?.lending_rules ?? {}),
        ...input.patch,
      },
      status_badge: existing?.status_badge,
      quantity: existing?.quantity,
      updated_at: new Date().toISOString(),
    };
    const merged = { ...state.itemExtras.byId, [input.itemId]: next };
    await persist(merged);
    return next;
  },
);

export const setItemQuantity = createAsyncThunk(
  'itemExtras/setItemQuantity',
  async (
    input: { itemId: string; quantity: number },
    { getState },
  ) => {
    // eslint-disable-next-line no-console
    console.log('🧪 itemExtrasSlice: setItemQuantity', input);
    const state = getState() as RootState;
    const existing = state.itemExtras.byId[input.itemId];
    const next: ItemExtras = {
      item_id: input.itemId,
      lending_rules: existing?.lending_rules ?? { ...DEFAULT_LENDING_RULES },
      status_badge: existing?.status_badge,
      quantity: Math.max(1, input.quantity),
      updated_at: new Date().toISOString(),
    };
    const merged = { ...state.itemExtras.byId, [input.itemId]: next };
    await persist(merged);
    return next;
  },
);

const itemExtrasSlice = createSlice({
  name: 'itemExtras',
  initialState,
  reducers: {
    clear: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateItemExtras.fulfilled, (state, action) => {
        state.byId = action.payload;
        state.hydrated = true;
      })
      .addCase(hydrateItemExtras.rejected, (state) => {
        state.hydrated = true;
      })
      .addCase(updateLendingRules.fulfilled, (state, action: PayloadAction<ItemExtras>) => {
        state.byId[action.payload.item_id] = action.payload;
      })
      .addCase(setItemQuantity.fulfilled, (state, action: PayloadAction<ItemExtras>) => {
        state.byId[action.payload.item_id] = action.payload;
      });
  },
});

export const { clear: clearItemExtras } = itemExtrasSlice.actions;

// Selectors
export const selectItemExtras = (itemId: string | number | null | undefined) =>
  (s: RootState): ItemExtras | null => {
    if (itemId === null || itemId === undefined) return null;
    return s.itemExtras.byId[String(itemId)] ?? null;
  };

export const selectLendingRules = (itemId: string | number | null | undefined) =>
  (s: RootState): LendingRules => {
    if (itemId === null || itemId === undefined) return { ...DEFAULT_LENDING_RULES };
    const extras = s.itemExtras.byId[String(itemId)];
    return extras?.lending_rules ?? { ...DEFAULT_LENDING_RULES };
  };

export default itemExtrasSlice.reducer;
export { DEFAULT_LENDING_RULES };
